// ConversationSidebar.tsx

import React from "react";
import { MessageCircle, Search, Plus, Star } from "lucide-react";
import { Contact, Conversation } from "../Types";
import { Avatar } from "../Avatar";
import SimpleBar from "simplebar-react";
import ConversationCreateModal from "../Modals/ConversationCreate";

// --- Conversation Item Component ---
interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: (conversation: Conversation) => void;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isActive,
  onClick,
}) => {
  return (
    <div
      className={`p-3 my-1 rounded-lg cursor-pointer transition-all border ${
        isActive
          ? "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-700"
          : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white border-transparent"
      }`}
      onClick={() => onClick(conversation)}
    >
      <div className="flex items-center space-x-3">
        <Avatar
          firstName={conversation?.contactId?.firstName}
          lastName={conversation?.contactId?.lastName}
          isActive={isActive}
          size="medium"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4
              className={`font-semibold text-sm truncate ${
                isActive
                  ? "text-blue-800 dark:text-blue-100"
                  : "text-gray-900 dark:text-white"
              }`}
            >
              {conversation?.contactId?.firstName}{" "}
              {conversation?.contactId?.lastName}
            </h4>
            {conversation?.isPinned && (
              <Star
                className={`w-4 h-4 ${
                  isActive
                    ? "text-blue-600 fill-current"
                    : "text-yellow-500 fill-current"
                }`}
              />
            )}
          </div>
          <p
            className={`text-xs truncate ${
              isActive
                ? "text-blue-600 dark:text-blue-300"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {conversation.lastMessage}
          </p>
        </div>
        <div className="text-right">
          <span
            className={`text-xs block ${
              isActive
                ? "text-blue-400 dark:text-blue-400"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {new Date(conversation.updatedAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
          {conversation.unreadCount > 0 && (
            <div
              className={`mt-1 ml-auto w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold bg-red-500 text-white`}
            >
              {conversation.unreadCount}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Conversation Sidebar Component ---
interface ConversationSidebarProps {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  handleConversationClick: (conversation: Conversation) => void;
}

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  activeConversation,
  searchTerm,
  setSearchTerm,
  handleConversationClick,
}) => {
  const pinnedConversations = conversations.filter((conv) => conv.isPinned);
  const otherConversations = conversations.filter((conv) => !conv.isPinned);

  return (
    <div className="w-80 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 flex flex-col shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <MessageCircle className="w-6 h-6 mr-2 text-blue-600" /> Chats
          </h2>
         <ConversationCreateModal handleConversationClick={handleConversationClick} conversations={conversations} />
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>
      </div>

      {/* Conversation List */}
      <SimpleBar className="max-h-[calc(100vh_-_330px)] xl:max-h-[calc(100vh_-_250px)]">
        <div className="lex-1 p-2">
          {/* Pinned Conversations */}
          {pinnedConversations.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 py-2">
                Pinned
              </h3>
              {pinnedConversations.map((conversation) => (
                <ConversationItem
                  key={conversation._id}
                  conversation={conversation}
                  isActive={activeConversation?._id === conversation._id}
                  onClick={handleConversationClick}
                />
              ))}
            </div>
          )}

          {/* All Conversations */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 py-2">
              All Conversations
            </h3>
            {otherConversations.map((conversation) => (
              <ConversationItem
                key={conversation._id}
                conversation={conversation}
                isActive={activeConversation?._id === conversation._id}
                onClick={handleConversationClick}
              />
            ))}
          </div>
        </div>
      </SimpleBar>
    </div>
  );
};
